import client from './client';
import { PROCESSES_API } from './routes';
import { openAuthenticatedSse } from './sse';
import type { ProcessGraphDto, ProcessArtifactDto, UserRefDto } from './processes';

// ── Run + gate enums (mirror backend RunStatus / RunNodeStatus / GateDecisionValue) ──

/**
 * Lifecycle state of a {@link ProcessRunDto}. Mirrors the backend enum
 * one-to-one (lower-snake-case wire format). Use {@link isTerminalRunStatus}
 * for the "no further transitions" check.
 */
export type RunStatus =
  | 'pending'
  | 'running'
  | 'waiting_for_action'
  | 'completed'
  | 'failed'
  | 'cancelled';

/** Per-node lifecycle state inside a run. */
export type RunNodeStatus =
  | 'pending'
  | 'provisioning'
  | 'running'
  | 'waiting_for_runner'
  | 'waiting_for_user'
  | 'completed'
  | 'failed'
  | 'skipped';

export type GateDecisionValue = 'approved' | 'rejected';

export const TERMINAL_RUN_STATUSES: ReadonlySet<RunStatus> = new Set<RunStatus>([
  'completed',
  'failed',
  'cancelled',
]);

export const isTerminalRunStatus = (status: RunStatus): boolean =>
  TERMINAL_RUN_STATUSES.has(status);

export const TERMINAL_NODE_STATUSES: ReadonlySet<RunNodeStatus> =
  new Set<RunNodeStatus>(['completed', 'failed', 'skipped']);

/**
 * A per-node status with no further transitions. The complement
 * (`pending` / `provisioning` / `running` / `waiting_for_runner` /
 * `waiting_for_user`) means the run is still working the node — i.e. it is
 * "active".
 */
export const isTerminalNodeStatus = (status: RunNodeStatus): boolean =>
  TERMINAL_NODE_STATUSES.has(status);

// ── Run input bindings ───────────────────────────────────────────────────────

/**
 * Discriminated union representing the value bound to a single input port
 * (one Output port per Input node) when a run starts. Mirrors
 * {@code RunInputValue} on the backend (sealed interface with three
 * variants). The backend deduces the variant from the unique field
 * (`uploaded` / `artifactId` / `text`), so each variant has exactly one
 * discriminating field.
 *
 * <ul>
 *   <li>{@code text} — inline text. Sent at {@link startPreparedRun} time
 *       (or {@link startNodeRun} for single-node runs); no presigned URL
 *       needed.</li>
 *   <li>{@code uploaded} — file input. The client must first declare the
 *       file at {@link prepareRun}, PUT the bytes to the returned URL,
 *       then send {@code uploaded:true} alongside the same {@code name}
 *       and {@code contentType} it used at prepare time. The two-phase
 *       flow keeps no server-side state about the upload; the server
 *       re-derives the canonical S3 key from these fields and HEADs it
 *       to capture the file's authoritative size. Single-node runs
 *       reject this variant by design — the editor's "Run from here"
 *       dialog only supports text + chained values.</li>
 *   <li>{@code artifactId} — chain to an existing run-bound artifact.
 *       Reserved for run-from-here / cascade flows.</li>
 * </ul>
 */
export type RunInputValue =
  | { readonly uploaded: true; readonly name: string; readonly contentType: string }
  | { readonly artifactId: string }
  | { readonly text: string };

/**
 * Body for {@code POST /processes/{id}/runs/{runId}:start}. Keys are
 * Input-node output port IDs.
 */
export interface StartRunRequest {
  readonly inputs: Readonly<Record<string, RunInputValue>>;
}

/**
 * One file the client is about to PUT directly to S3 in
 * {@link prepareRun}. Mirrors {@code PrepareRunInputDescriptor} on the
 * backend.
 *
 * <p>The descriptor carries only what the server needs to mint a
 * presigned URL: the file's {@code name} (which becomes the leaf segment
 * of the canonical S3 key) and its {@code contentType} (baked into the
 * URL signature, so the client must PUT with the matching
 * {@code Content-Type} header). The actual size is captured on the
 * server side from the S3 HEAD response after the upload lands -- the
 * client is never trusted on size, so there's no point sending it
 * here.</p>
 */
export interface PrepareRunInputDescriptor {
  readonly name: string;
  readonly contentType: string;
}

/**
 * Body for {@code POST /processes/{id}/runs:prepare}. Keys are Input-node
 * output port IDs; one entry per file-typed input. Text inputs are
 * intentionally absent — they don't need a presigned URL and travel
 * inline at start time.
 *
 * {@code expectedUpdatedAt} pins the graph version the dialog built its
 * rows from; a replaced port id then comes back as 409 DRIFT_DETECTED
 * before any run row or upload slot exists.
 */
export interface PrepareRunRequest {
  readonly fileInputs: Readonly<Record<string, PrepareRunInputDescriptor>>;
  readonly expectedUpdatedAt?: string;
}

/**
 * One presigned PUT slot returned by {@link prepareRun}. The client PUTs
 * the file bytes to {@code uploadUrl} with the headers in
 * {@code uploadHeaders} (typically just {@code Content-Type}).
 */
export interface PrepareRunUploadSlot {
  readonly uploadUrl: string;
  readonly uploadHeaders: Readonly<Record<string, string>>;
  readonly expiresAt: string;
}

/**
 * Response of {@link prepareRun}: the freshly-created PENDING run id plus
 * one upload slot per file-typed input port.
 */
export interface PrepareRunResponse {
  readonly runId: string;
  readonly uploads: Readonly<Record<string, PrepareRunUploadSlot>>;
}

// ── Run DTOs (mirror backend records) ────────────────────────────────────────

export interface ProcessRunDto {
  readonly runId: string;
  readonly processId: string;
  readonly runNumber: number;
  readonly status: RunStatus;
  readonly isTest: boolean;
  /**
   * Node id this run is bounded to, or `null` for a full-graph run. When
   * non-null the engine drives a topologically-bounded cascade ending at
   * the target node; the editor reads this on reconnect to choose between
   * the full-run and node-run UX.
   */
  readonly targetNodeId: string | null;
  readonly startedBy: UserRefDto;
  readonly inputs: Readonly<Record<string, RunInputValue>>;
  readonly failureReason: string | null;
  readonly createdAt: string;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
  readonly cancelledAt: string | null;
  readonly updatedAt: string;
}

export interface ProcessRunNodeStateDto {
  readonly nodeStateId: string;
  readonly nodeId: string;
  readonly iteration: number;
  readonly status: RunNodeStatus;
  readonly errorMessage: string | null;
  /** Raw executor text behind {@link errorMessage}, shown behind the dock's
   *  "Technical details" disclosure. Null when there is nothing to reveal. */
  readonly errorDetail?: string | null;
  readonly executionTimeMs: number | null;
  /** Credits billed for this attempt; null/absent when nothing was billed. */
  readonly creditsSpent?: number | null;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
  readonly artifacts: readonly ProcessArtifactDto[];
  readonly hasTranscript: boolean;
}

export interface GateDecisionDto {
  readonly decisionId: string;
  readonly nodeId: string;
  readonly iteration: number;
  readonly decision: GateDecisionValue;
  readonly decidedBy: UserRefDto;
  readonly isTimeout: boolean;
  readonly isAuto: boolean;
  readonly comment: string | null;
  readonly decidedAt: string;
}

export interface ProcessRunDetailDto {
  readonly run: ProcessRunDto;
  readonly graphSnapshot: ProcessGraphDto;
  readonly nodeStates: readonly ProcessRunNodeStateDto[];
  readonly gateDecisions: readonly GateDecisionDto[];
}

export interface ProcessRunListItemDto {
  readonly runId: string;
  readonly runNumber: number;
  readonly status: RunStatus;
  readonly isTest: boolean;
  /** Non-null for single-node runs (cascade ending at this node). */
  readonly targetNodeId: string | null;
  readonly startedBy: UserRefDto;
  readonly createdAt: string;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
  readonly durationMs: number | null;
  readonly failureReason: string | null;
}

export interface ProcessRunListResponse {
  readonly runs: readonly ProcessRunListItemDto[];
  readonly page: number;
  readonly size: number;
  readonly totalElements: number;
  readonly totalPages: number;
}

export interface GateDecideRequest {
  readonly iteration: number;
  readonly decision: GateDecisionValue;
  readonly comment?: string;
}

export interface ArtifactDownloadUrlResponse {
  readonly downloadUrl: string;
  readonly expiresAt: string;
  readonly contentType: string;
  readonly fileSize: number;
}

/**
 * Body for {@code POST /processes/{id}/runs/{runId}/manual/{nodeId}/outputs:prepare}.
 * Keys are the manual node's output port IDs; one descriptor per file the
 * assignee intends to upload. Mirrors {@link PrepareRunRequest} — the server
 * mints one presigned PUT slot per entry.
 */
export interface ManualOutputPrepareRequest {
  readonly outputs: Readonly<Record<string, PrepareRunInputDescriptor>>;
}

/** Response of {@link prepareManualOutputs}: one presigned slot per output port. */
export interface ManualOutputPrepareResponse {
  readonly uploads: Readonly<Record<string, PrepareRunUploadSlot>>;
}

/** One uploaded output binding in a {@link ManualCompleteRequest}. */
export interface ManualOutputUpload {
  readonly portId: string;
  readonly name: string;
  readonly contentType: string;
}

/**
 * Body for {@code POST /processes/{id}/runs/{runId}/manual/{nodeId}/complete}.
 * {@code iteration} guards against completing a stale iteration; each entry in
 * {@code outputs} binds an uploaded file to one of the node's output ports.
 */
export interface ManualCompleteRequest {
  readonly iteration: number;
  readonly outputs: readonly ManualOutputUpload[];
}

// ── Endpoints ────────────────────────────────────────────────────────────────

const BASE = PROCESSES_API;

const url = (...parts: readonly string[]): string =>
  `${BASE}/${parts.map(encodeURIComponent).join('/')}`;

export interface PrepareRunOptions {
  /** Whether to mark the run as a test run (`?test=true`). Default: false. */
  readonly test?: boolean;
}

/**
 * Phase 1 of the two-phase run-start flow. Creates a PENDING run row and
 * returns one presigned PUT URL per file-typed input port (each scoped to
 * the run's S3 prefix). The client then PUTs each file directly to S3 and
 * calls {@link startPreparedRun} with the resolved input map (text inline,
 * file inputs as {@code {uploaded: true}}).
 *
 * <p>The backend coerces runs of unpublished processes to {@code isTest=true},
 * so callers must read the resolved status from the {@link startPreparedRun}
 * response rather than trusting {@code opts.test}.</p>
 */
export async function prepareRun(
  processId: string,
  body: PrepareRunRequest,
  opts: PrepareRunOptions = {},
): Promise<PrepareRunResponse> {
  const { data } = await client.post<PrepareRunResponse>(
    `${url(processId)}/runs:prepare`,
    body,
    { params: { test: opts.test ?? false } },
  );
  return data;
}

/**
 * Phase 2 of the two-phase run-start flow. Materialises every declared
 * input port (text inline, uploaded via HEAD on the prepared S3 key,
 * chained via reference to an upstream artifact), snapshots the graph,
 * and dispatches the engine.
 */
export async function startPreparedRun(
  processId: string,
  runId: string,
  body: StartRunRequest,
): Promise<ProcessRunDto> {
  const { data } = await client.post<ProcessRunDto>(
    `${url(processId, 'runs', runId)}:start`,
    body,
  );
  return data;
}

export interface ListRunsParams {
  /** Comma-separated list of {@link RunStatus} tokens. Unknown tokens are dropped server-side. */
  readonly status?: string;
  /** Restrict to test runs (`true`) / production runs (`false`). Omit for both. */
  readonly isTest?: boolean;
  readonly page?: number;
  readonly size?: number;
}

export async function listRuns(
  processId: string,
  params: ListRunsParams = {},
): Promise<ProcessRunListResponse> {
  const { data } = await client.get<ProcessRunListResponse>(
    url(processId, 'runs'),
    {
      params: {
        ...(params.status ? { status: params.status } : {}),
        ...(params.isTest === undefined ? {} : { isTest: params.isTest }),
        page: params.page ?? 0,
        size: params.size ?? 20,
      },
    },
  );
  return data;
}

export async function getRun(
  processId: string,
  runId: string,
): Promise<ProcessRunDetailDto> {
  const { data } = await client.get<ProcessRunDetailDto>(
    url(processId, 'runs', runId),
  );
  return data;
}

export async function cancelRun(
  processId: string,
  runId: string,
): Promise<ProcessRunDto> {
  const { data } = await client.post<ProcessRunDto>(
    url(processId, 'runs', runId, 'cancel'),
  );
  return data;
}

export async function decideGate(
  processId: string,
  runId: string,
  nodeId: string,
  body: GateDecideRequest,
): Promise<GateDecisionDto> {
  const { data } = await client.post<GateDecisionDto>(
    url(processId, 'runs', runId, 'gate', nodeId, 'decide'),
    body,
  );
  return data;
}

/**
 * Records the caller's consent to run this process's local task work on
 * their machine and resumes the run so the allocator can claim an eligible
 * runner. The backend authorises the caller against the waiting nodes'
 * frozen assignee snapshot (403 for non-assignees).
 */
export async function acceptRunnerClaim(
  processId: string,
  runId: string,
): Promise<ProcessRunDto> {
  const { data } = await client.post<ProcessRunDto>(
    url(processId, 'runs', runId, 'runner-claim', 'accept'),
  );
  return data;
}

/**
 * Phase 1 of the manual-node completion flow. Mints one presigned PUT slot
 * per declared output port so the assignee can upload their off-line work
 * directly to S3, then calls {@link completeManualNode}. The backend
 * authorises the caller against the node's frozen assignee snapshot.
 */
export async function prepareManualOutputs(
  processId: string,
  runId: string,
  nodeId: string,
  body: ManualOutputPrepareRequest,
): Promise<ManualOutputPrepareResponse> {
  const { data } = await client.post<ManualOutputPrepareResponse>(
    `${url(processId, 'runs', runId, 'manual', nodeId)}/outputs:prepare`,
    body,
  );
  return data;
}

/**
 * Phase 2 of the manual-node completion flow. Binds the uploaded output
 * artifacts (sizes re-read from S3, never trusted from the client) to the
 * node's output ports and resumes the run.
 */
export async function completeManualNode(
  processId: string,
  runId: string,
  nodeId: string,
  body: ManualCompleteRequest,
): Promise<ProcessRunDto> {
  const { data } = await client.post<ProcessRunDto>(
    url(processId, 'runs', runId, 'manual', nodeId, 'complete'),
    body,
  );
  return data;
}

export interface ListRunArtifactsParams {
  readonly nodeId?: string;
  readonly portId?: string;
  readonly iteration?: number;
}

export async function listRunArtifacts(
  processId: string,
  runId: string,
  params: ListRunArtifactsParams = {},
): Promise<readonly ProcessArtifactDto[]> {
  const { data } = await client.get<readonly ProcessArtifactDto[]>(
    url(processId, 'runs', runId, 'artifacts'),
    { params },
  );
  return data;
}

export async function getArtifactDownloadUrl(
  processId: string,
  artifactId: string,
): Promise<ArtifactDownloadUrlResponse> {
  const { data } = await client.get<ArtifactDownloadUrlResponse>(
    url(processId, 'artifacts', artifactId, 'download-url'),
  );
  return data;
}

export async function cancelNodeRun(
  processId: string,
  nodeId: string,
): Promise<void> {
  await client.post(url(processId, 'nodes', nodeId, 'cancel'));
}

// ── SSE consumers (raw fetch + AbortSignal so useSseStream can drive them) ──

export interface SubscribeToRunStreamOptions {
  /** Caller-owned signal so React can cancel cleanly on unmount. */
  readonly signal: AbortSignal;
}

/**
 * Opens the GET SSE stream for a full-graph run. Returns the raw
 * `Response` -- the body is a `ReadableStream<Uint8Array>` that
 * {@link useSseStream} can iterate over. Caller must {@code abort} the
 * supplied signal to close the stream.
 *
 * <p>The stream is <strong>live-only</strong>: there is no server-side
 * replay buffer. If the connection drops, the client should reconnect
 * to pick up further live events. Persistent run state (per-node
 * statuses, artifacts, gate decisions) is reloaded by re-fetching the
 * detail endpoint, not by replaying SSE history.</p>
 */
export async function subscribeToRunStream(
  processId: string,
  runId: string,
  opts: SubscribeToRunStreamOptions,
): Promise<Response> {
  const path = url(processId, 'runs', runId, 'stream');
  return openAuthenticatedSse(path, {
    method: 'GET',
    signal: opts.signal,
  });
}

export interface StartNodeRunOptions {
  /** Optional value the user typed / picked when running an INPUT node.
   *  Backend materialises it as a run-bound artifact on the new test run
   *  before dispatching. */
  readonly inputValue?: RunInputValue;
}

/**
 * Starts a single-node test run. Returns the freshly-created
 * {@link ProcessRunDto} synchronously (HTTP 201); the caller subscribes
 * to the unified {@link subscribeToRunStream} SSE channel keyed on
 * {@code result.runId} for live updates.
 *
 * <p>Upstream Task nodes that don't already have complete outputs from a
 * previous test run are run first by the backend in topological order --
 * there is no opt-out. The optional JSON body carries {@code inputValue},
 * only meaningful when the target node is an INPUT.</p>
 */
export async function startNodeRun(
  processId: string,
  nodeId: string,
  opts: StartNodeRunOptions = {},
): Promise<ProcessRunDto> {
  const path = url(processId, 'nodes', nodeId, 'run');
  const body = opts.inputValue ? { inputValue: opts.inputValue } : {};
  const { data } = await client.post<ProcessRunDto>(path, body);
  return data;
}

// ── Node transcript ─────────────────────────────────────────────────────────

export interface TranscriptEntryDto {
  readonly type: 'text' | 'tool';
  readonly text?: string;
  readonly tool?: string;
  readonly toolUseId?: string | null;
  readonly status?: string;
  readonly success?: boolean;
  readonly summary?: string;
  /** Tool input as a JSON string — same shape as chat's `approvalArgs`. */
  readonly args?: string;
  readonly timestamp: string;
}

export async function getNodeTranscript(
  processId: string,
  runId: string,
  nodeId: string,
  iteration = 0,
): Promise<readonly TranscriptEntryDto[]> {
  const { data } = await client.get<readonly TranscriptEntryDto[]>(
    url(processId, 'runs', runId, 'nodes', nodeId, 'transcript'),
    { params: { iteration } },
  );
  return data;
}
